"use client";

import type { TemplateDetails } from "@/features/calendar/types/templates";

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "motion/react";

import { type CalendarConfig, defaultConfig } from "../../types/calendar-config";

import { TemplateSlotComponent } from "./TemplateSlotComponent.tsx";
import { TemplateSlotDialog } from "./TemplateSlotDialog.tsx";
import { TemplateSlotEditDialog } from "./TemplateSlotEditDialog.tsx";

import { CalendarGridBase } from "@/features/calendar/components/shared/CalendarGridBase";
import { CalendarHourCell } from "@/features/calendar/components/shared/CalendarHourCell";
import { Button } from "@/components/ui/button";
import { useTemplatesStore } from "@/features/calendar/store/useTemplatesStore";
import GenericLoader from "@/components/GenericLoader.tsx";

interface TemplateCalendarViewProps {
  template: TemplateDetails;
  config?: Partial<CalendarConfig>;
}

export function TemplateCalendarView({ template, config = {} }: TemplateCalendarViewProps) {
  const { t } = useTranslation(["common", "calendar"]);
  const finalConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);
  const [isSlotDialogOpen, setIsSlotDialogOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState<number | undefined>();
  const [selectedDay, setSelectedDay] = useState<number | undefined>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any | null>(null);

  // Use the templates store
  const { templateSlots, isLoadingSlots, fetchTemplateSlots } = useTemplatesStore();

  const hours = useMemo(
    () =>
      Array.from(
        { length: finalConfig.maxHour - finalConfig.minHour },
        (_, i) => i + finalConfig.minHour,
      ),
    [finalConfig.maxHour, finalConfig.minHour],
  );

  // Define days of the week
  const daysOfWeek = useMemo(() => {
    if (template.type === "weekly") {
      return [
        { day: 1, name: t("calendar:days.monday"), shortName: "1" },
        { day: 2, name: t("calendar:days.tuesday"), shortName: "2" },
        { day: 3, name: t("calendar:days.wednesday"), shortName: "3" },
        { day: 4, name: t("calendar:days.thursday"), shortName: "4" },
        { day: 5, name: t("calendar:days.friday"), shortName: "5" },
        { day: 6, name: t("calendar:days.saturday"), shortName: "6" },
        { day: 7, name: t("calendar:days.sunday"), shortName: "7" },
      ];
    } else {
      // For daily templates, just show one day
      return [
        {
          day: 0,
          name: t("calendar:templates.dailySchedule"),
          shortName: t("calendar:templates.daily"),
        },
      ];
    }
  }, [t, template.type]);

  // Get slots for a specific day
  const getSlotsForDay = useCallback(
    (day: number) => {
      if (!templateSlots || templateSlots.length === 0) return [];

      return templateSlots.filter((slot) => {
        if (template.type === "weekly") {
          return slot.day_of_week === day;
        } else {
          // For daily templates, all slots belong to the single day
          return true;
        }
      });
    },
    [templateSlots, template.type],
  );

  // Convert template slots to a format similar to calendar slots
  const convertToCalendarSlot = useCallback((slot: any, day: number) => {
    const startTime = slot.start_time;
    const endTime = slot.end_time;

    return {
      id: slot.id,
      startTime: startTime,
      endTime: endTime,
      startTimeDateTimeString: `2023-01-0${day}T${startTime}:00`,
      endTimeDateTimeString: `2023-01-0${day}T${endTime}:00`,
      status: "available",
      lessonType: slot.lesson_type,
      lessonHost: slot.lesson_host,
      topic: slot.topic,
      // Add template-specific properties
      isTemplateSlot: true,
      templateSlot: slot,
    };
  }, []);

  // Handle opening the slot dialog
  const handleAddSlot = (hour: number, day: number) => {
    setSelectedHour(hour);
    setSelectedDay(day);
    setIsSlotDialogOpen(true);
  };

  const handleEditSlot = (slot: any) => {
    setSelectedSlot(slot);
    setIsEditDialogOpen(true);
  };

  // Check if a slot exists at a specific hour and day
  const hasSlotAtTime = useCallback(
    (hour: number, day: number) => {
      const daySlots = getSlotsForDay(day);

      return daySlots.some((slot) => {
        const startHour = Number.parseInt(slot.start_time.split(":")[0]);
        const endHour = Number.parseInt(slot.end_time.split(":")[0]);

        // Check if the hour falls within the slot's time range
        return hour >= startHour && hour < endHour;
      });
    },
    [getSlotsForDay],
  );

  // Handle successful slot creation or update
  const handleSlotSuccess = () => {
    fetchTemplateSlots(template.id);
  };

  return (
    <>
      <CalendarGridBase
        headerHeight={finalConfig.headerHeight}
        hourHeight={finalConfig.hourHeight}
        hours={hours}
        isLoading={false}
        isWeeklyTemplate={template.type === "weekly"}
        minHour={finalConfig.minHour}
        shownDays={daysOfWeek.length}
        timeColumnWidth={finalConfig.timeColumnWidth}
        weekDays={daysOfWeek.map((day) => {
          // Create fake date objects for the calendar grid
          const date = new Date(2023, 0, day.day || 1);

          // Add custom properties for template days
          Object.assign(date, {
            dayName: day.name,
            shortName: day.shortName,
            dayOfWeek: day.day,
          });

          return date;
        })}
      >
        {isLoadingSlots && <GenericLoader className={"fixed!"} />}
        {daysOfWeek.map((day, dayIndex) => (
          <div key={`day-${day.day}`} className="relative">
            {hours.map((hour) => (
              <CalendarHourCell key={hour} hour={hour} isLoading={false}>
                {!hasSlotAtTime(hour, day.day) && (
                  <Button
                    className="border-muted-foreground/20 hover:border-sidebar/40 h-full w-full border-2 border-dashed bg-transparent opacity-0 transition-opacity duration-200 hover:opacity-100"
                    onClick={() => handleAddSlot(hour, day.day)}
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-md">
                      <span className="iconify tabler--plus size-6" />
                    </div>
                  </Button>
                )}
              </CalendarHourCell>
            ))}

            <AnimatePresence initial={true} mode="popLayout">
              {getSlotsForDay(day.day).map((slot) => {
                const calendarSlot = convertToCalendarSlot(slot, day.day || 1);

                return (
                  <TemplateSlotComponent
                    key={slot.id}
                    dayIndex={dayIndex}
                    event={calendarSlot}
                    finalConfig={finalConfig}
                    templateId={template.id}
                    templateType={template.type}
                    weekDaysLength={daysOfWeek.length}
                    onEditSlot={handleEditSlot}
                    onSlotDeleted={handleSlotSuccess}
                  />
                );
              })}
            </AnimatePresence>
          </div>
        ))}
      </CalendarGridBase>

      {/* Slot Dialog */}
      <TemplateSlotDialog
        initialDay={selectedDay}
        initialHour={selectedHour}
        isOpen={isSlotDialogOpen}
        templateId={template.id}
        templateType={template.type}
        onClose={() => setIsSlotDialogOpen(false)}
        onSuccess={handleSlotSuccess}
      />

      {/* Edit Slot Dialog */}
      <TemplateSlotEditDialog
        isOpen={isEditDialogOpen}
        slotData={selectedSlot}
        templateId={template.id}
        templateType={template.type}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleSlotSuccess}
      />
    </>
  );
}
